import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
} from 'reactstrap';
import { PromoCode } from '@src/@data/types/promoCodes';
import { useMessages } from '@hooks/useMessages';
import { StepBody, StepFooter } from '@appcomponents/atoms/SidebarStepTemplate';
import Avatar from '@src/components/atoms/avatar';
import { User } from 'react-feather';
import { formatName } from '@src/@data/utility/names';
import { CalendarSelector } from '@appcomponents/molecules/CalendarSelector';
import { UILoader } from '@src/components/atoms/ui-loader';
import { CalendarType } from '@src/@data/types/calendars';
import { ServiceSelector } from '@src/components/molecules/service-selector/ServiceSelector';
import { DateAndTimeFormGroup } from '@src/pages/company/reservations/modals/components/date-and-time-form-group/DateAndTimeFormGroup';
import { useTimeslots } from '@src/pages/company/reservations/modals/components/timeslotUtils';
import reservationsApi from '@src/@data/api/reservations';
import { convertToApiDate } from '@src/@data/utility/dates';
import { useCreateReservation } from '@appcomponents/organisms/create-reservation/useCreateReservation';
import NumberInput from '@appcomponents/atoms/NumberInput';
import {
  CreateConfirmedReservationRequest,
  GetTimeslotsParams,
} from '@src/@data/types/reservations';
import { TimeslotOption } from '@src/pages/company/reservations/modals/components/TimeSelector';
import { toastOnError } from '@src/@data/utility/errors';
import {
  getPath,
  ROUTE_RESERVATION_SLOT,
  ROUTE_RESERVATION_TIME,
} from '@src/@data/router/consts';
import { useHistory } from 'react-router';
import servicesApi from '@src/@data/api/services';
import { CustomForm } from './custom-form/CustomForm';
import { CustomFormWithValues } from '@src/@data/types/customForms';
import {
  TicketsSelector,
  TicketWithSpots,
} from '@src/components/molecules/select-tickets/TicketsSelector';

interface CreateReservationFromPromoProps {
  companyId: string;
  promoCode: PromoCode;
  isOpen: boolean;
  onCancel: () => void;
}

export const CreateReservationFromPromoModal: React.FC<CreateReservationFromPromoProps> =
  ({ companyId, promoCode, isOpen, onCancel }) => {
    const { messages } = useMessages();
    const history = useHistory();

    const [isSaving, setIsSaving] = useState(false);

    const client = useMemo(() => promoCode.client, [promoCode.client]);

    const [startDate, setStartDate] = useState<Date>(new Date());
    const [startTime, setStartTime] = useState<Date | undefined>();

    const {
      isLoading,
      availableCalendars,
      availableServices,
      selectedService,
      selectedCalendar,
      setSelectedService,
      setSelectedCalendar,
    } = useCreateReservation(
      companyId,
      promoCode.serviceId,
      promoCode.calendarId
    );

    const [spots, setSpots] = useState(1);

    const isSlot = useMemo(
      () => selectedCalendar?.type === CalendarType.Slot,
      [selectedCalendar?.type]
    );

    const isTime = useMemo(
      () => selectedCalendar?.type === CalendarType.Time,
      [selectedCalendar?.type]
    );

    // Tickets

    const [isTicketService, setIsTicketService] = useState<boolean | null>(
      false
    );

    const [ticketsWithCount, setTicketsWithCount] = useState<TicketWithSpots[]>(
      []
    );

    const updateTicketsCount = useCallback((tickets: TicketWithSpots[]) => {
      setTicketsWithCount(tickets);
      setSpots(tickets.reduce((acc, t) => acc + t.spots, 0));
    }, []);

    useEffect(() => {
      setIsTicketService(null);
      updateTicketsCount([]);
      if (!selectedService) {
        return;
      }
      servicesApi
        .getBusinessServiceTickets(companyId, selectedService.id)
        .then((response) => {
          updateTicketsCount(
            response.map((ticket) => ({ ...ticket, spots: 0 }))
          );
          setIsTicketService(response.length > 0);
        })
        .catch(toastOnError);
    }, [companyId, selectedService, updateTicketsCount]);

    // Timeslots

    const resetStartTime = useCallback(() => {
      setStartTime(undefined);
    }, []);

    const getTimeslots = useCallback(() => {
      if (!selectedService || !selectedCalendar) {
        return Promise.resolve(undefined);
      }
      const request: GetTimeslotsParams = {
        companyId,
        date: convertToApiDate(startDate),
      };
      if (isSlot) {
        request.slotParams = {
          calendarId: selectedCalendar.id,
          serviceId: selectedService.id,
          spots,
          tickets: ticketsWithCount
            .filter((t) => t.spots)
            .map((t) => ({
              ticketId: t.id,
              spots: t.spots,
            })),
        };
      } else {
        request.timeReservationsParams = {
          calendarId: selectedCalendar.id,
          duration: selectedService.duration,
        };
      }
      return reservationsApi.getTimeslots(request);
    }, [
      companyId,
      isSlot,
      selectedCalendar,
      selectedService,
      ticketsWithCount,
      spots,
      startDate,
    ]);

    const { timeslots, areTimeslotsLoading } = useTimeslots({
      isReady: isSlot && !!selectedService && isTicketService !== null,
      getTimeslots,
      resetStartTime,
    });

    const selectedTimeslot = useMemo<TimeslotOption | null>(() => {
      if (!startTime) {
        return null;
      }
      return timeslots?.find((t) => t.value === startTime) || null;
    }, [startTime, timeslots]);

    const slotTickets = useMemo<
      { slotTicketId: string; spots: number }[]
    >(() => {
      if (
        !isTicketService ||
        !ticketsWithCount.length ||
        !selectedTimeslot?.tickets?.length
      ) {
        return [];
      }
      return ticketsWithCount
        .filter((t) => t.spots)
        .map((t) => ({
          slotTicketId:
            selectedTimeslot.tickets?.find((tt) => tt.serviceTicketId === t.id)
              ?.id ?? '',
          spots: t.spots,
        }));
    }, [isTicketService, ticketsWithCount, selectedTimeslot]);

    // Rest

    const [note, setNote] = useState<string>('');
    const [customFormValues, setCustomFormValues] =
      useState<CustomFormWithValues>();

    // Handlers

    const isValid = useMemo(() => {
      if (isSlot) {
        if (
          !selectedTimeslot?.slotId ||
          !selectedService ||
          !selectedCalendar
        ) {
          return false;
        }
        if (isTicketService) {
          if (spots === 0) {
            return false;
          } else if (
            promoCode.spotsPerUseLimit > 0 &&
            spots > promoCode.spotsPerUseLimit
          ) {
            return false;
          }
        } else {
          if (
            promoCode.spotsPerUseLimit > 0 &&
            spots > promoCode.spotsPerUseLimit
          ) {
            return false;
          }
        }
        return true;
      } else if (isTime) {
        return !!selectedService && !!selectedCalendar && !!selectedTimeslot;
      }
      return false;
    }, [
      isSlot,
      isTicketService,
      isTime,
      promoCode.spotsPerUseLimit,
      selectedCalendar,
      selectedService,
      selectedTimeslot,
      spots,
    ]);

    const goToReservationDetails = useCallback(
      (type: CalendarType, id: string) => {
        history.push(
          type === CalendarType.Time
            ? getPath(ROUTE_RESERVATION_TIME, {
                companyId,
                reservationId: id,
              })
            : getPath(ROUTE_RESERVATION_SLOT, {
                companyId,
                slotId: id || '',
              })
        );
      },
      [companyId, history]
    );

    const handleSubmit = useCallback(() => {
      setIsSaving(true);
      let request: CreateConfirmedReservationRequest;
      if (isSlot) {
        request = {
          type: CalendarType.Slot,
          calendarId: selectedCalendar?.id,
          serviceId: selectedService?.id,
          slotId: selectedTimeslot?.slotId ?? '',
          spots,
          comment: note,
          tickets: slotTickets,
          promoCodeId: promoCode.id,
        };
      } else if (isTime) {
        request = {
          type: CalendarType.Time,
          calendarId: selectedCalendar?.id ?? '',
          serviceId: selectedService?.id ?? '',
          startDate: selectedTimeslot?.value
            ? convertToApiDate(selectedTimeslot.value)
            : '',
          duration: selectedTimeslot?.duration,
          comment: note,
          promoCodeId: promoCode.id,
        };
      } else {
        return;
      }
      request.comment = note;
      request.customForm = customFormValues;

      reservationsApi
        .createConfirmedReservation(companyId, request)
        .then((res) => {
          goToReservationDetails(request.type, res.slotId ?? res.id);
        })
        .catch((e) => {
          toastOnError(e);
        })
        .finally(() => {
          setIsSaving(false);
        });
    }, [
      companyId,
      goToReservationDetails,
      isSlot,
      isTime,
      note,
      promoCode.id,
      selectedCalendar,
      selectedService,
      selectedTimeslot,
      slotTickets,
      spots,
      customFormValues,
    ]);

    return (
      <Modal
        isOpen={isOpen}
        toggle={() => onCancel}
        unmountOnClose={true}
        centered
        size="lg"
      >
        <ModalHeader>{messages('dialog_action_make_reservations')}</ModalHeader>
        <ModalBody>
          {!isLoading && (
            <UILoader blocking={isSaving} className="d-flex flex-column h-100">
              <StepBody>
                {client && (
                  <FormGroup>
                    <Label for="client-selector" className="text-secondary">
                      {messages('label_client')}
                    </Label>
                    <div>
                      <Avatar
                        img={client.thumbnail}
                        icon={
                          !client.thumbnail ? <User size={20} /> : undefined
                        }
                        className="mr-75"
                      />
                      <span>
                        {formatName(client.firstName, client.lastName)}
                      </span>
                    </div>
                  </FormGroup>
                )}
                <FormGroup>
                  <Label for="calendar-selector" className="text-secondary">
                    {messages('label_calendar')}
                  </Label>
                  <CalendarSelector
                    id="calendar-selector"
                    calendars={availableCalendars}
                    selectedCalendar={selectedCalendar}
                    setSelectedCalendar={setSelectedCalendar}
                    disabled={!!promoCode.calendarId}
                    menuMaxHeight={200}
                  />
                </FormGroup>
                <FormGroup>
                  <Label for="service-selector" className="text-secondary">
                    {messages('label_service')}
                  </Label>
                  <ServiceSelector
                    services={availableServices}
                    selectedService={selectedService}
                    setSelectedService={setSelectedService}
                    resolveServiceThumbnail={(service) =>
                      service.image?.thumbnail
                    }
                    disabled={!!promoCode.serviceId}
                    menuMaxHeight={150}
                  />
                </FormGroup>
                {isSlot && isTicketService === false && (
                  <FormGroup>
                    <Label for="spots-selector" className="text-secondary">
                      {messages('label_spots')}
                    </Label>
                    <NumberInput
                      value={spots}
                      onChange={setSpots}
                      max={
                        promoCode.spotsPerUseLimit > 0
                          ? promoCode.spotsPerUseLimit
                          : undefined
                      }
                    />
                  </FormGroup>
                )}
                {isSlot && isTicketService === true && (
                  <FormGroup>
                    <Label for="tickets-selector" className="text-secondary">
                      {messages('label_tickets')}
                    </Label>
                    <TicketsSelector
                      tickets={ticketsWithCount}
                      onUpdated={updateTicketsCount}
                      maxSpots={promoCode.spotsPerUseLimit || Infinity}
                      isSingleTicket={!!selectedService?.singleTicket}
                    />
                  </FormGroup>
                )}
                <DateAndTimeFormGroup
                  row={{
                    startDate,
                    startTime,
                    setStartTime,
                    setStartDate,
                    timeslots,
                    areTimeslotsLoading,
                  }}
                  areTimeslotsDisabled={!isSlot}
                  dateChangeDisabled={false}
                />
                <FormGroup>
                  <Label for="note-input" className="text-secondary">
                    {messages('label_note')}
                  </Label>
                  <Input
                    id="note-input"
                    type="text"
                    value={note}
                    onChange={(e) => setNote(e.currentTarget.value)}
                    autoFocus
                  />
                </FormGroup>
                <CustomForm
                  customFormId={selectedService?.customFormId}
                  companyId={companyId}
                  client={promoCode.client}
                  customFormValues={customFormValues}
                  setCustomFormValues={setCustomFormValues}
                />
              </StepBody>
              <StepFooter>
                <Button
                  onClick={() => onCancel()}
                  color="secondary"
                  className="mr-1"
                >
                  {messages('dialog_action_cancel')}
                </Button>
                <Button
                  disabled={!isValid}
                  onClick={() => handleSubmit()}
                  color="primary"
                >
                  {messages('dialog_action_save')}
                </Button>
              </StepFooter>
            </UILoader>
          )}
        </ModalBody>
      </Modal>
    );
  };

export default CreateReservationFromPromoModal;
